import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { DatePicker, Form, Space, Spin } from 'antd'
import {
  capitalize,
  difference,
  filter,
  find,
  flatten,
  get,
  includes,
  intersection,
  isEmpty,
  isNil,
  keys,
  map,
  omit,
  size,
  uniqBy,
} from 'lodash'
import moment from 'moment'
import PropTypes from 'prop-types'

import { FormButtons, FormSelect } from '../../../common/components/sections'
import {
  DROPDOWN_MIN_WIDTH,
  EVENT_METADATA_TYPES,
  EVENT_SUBTYPES_GROUPED_BY_EVENT_METADATA_TYPE,
  EVENT_TYPES,
  PERMISSIONS,
} from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import { useFormDirty, useUsersQuery } from '../../../common/hooks'
import {
  filterSelectOptions,
  getFormItemLabelAboveConfiguration,
} from '../../../common/utils'

const UsersReportForm = ({ isGeneratingReport, onSubmit }) => {
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)
  const [selectedEntityTypes, setSelectedEntityTypes] = useState([])
  const [selectedSubtypes, setSelectedSubtypes] = useState([])

  const {
    options: userOptions,
    isLoading: isFetchUsersLoading,
    error: fetchUsersError,
  } = useUsersQuery()

  const entityTypeOptions = useMemo(
    () =>
      map(keys(EVENT_METADATA_TYPES), eventType => ({
        value: eventType,
        label: capitalize(get(EVENT_METADATA_TYPES, eventType)).replaceAll(
          '_',
          ' ',
        ),
      })),
    [],
  )

  const typeOptions = useMemo(
    () =>
      map(keys(EVENT_TYPES), eventType => ({
        value: eventType,
        label: capitalize(get(EVENT_TYPES, eventType)).replaceAll('_', ' '),
      })),
    [],
  )

  const subtypeOptions = useMemo(
    () =>
      uniqBy(
        flatten(
          map(
            filter(keys(EVENT_SUBTYPES_GROUPED_BY_EVENT_METADATA_TYPE), type =>
              includes(selectedEntityTypes, type),
            ),
            type => get(EVENT_SUBTYPES_GROUPED_BY_EVENT_METADATA_TYPE, type),
          ),
        ),
        'value',
      ),
    [selectedEntityTypes],
  )

  const datePickerRangeOptions = useMemo(
    () => ({
      Today: [moment().startOf('day'), moment().endOf('day')],
      'This Month': [moment().startOf('month'), moment().endOf('month')],
    }),
    [],
  )

  const handleFilter = useCallback(
    data => {
      const { dateRange } = data

      const values = {
        ...omit(data, ['dateRange']),
        startAt: get(dateRange, '[0]', null)?.format('YYYY-MM-DD HH:mm:ss'),
        endAt: get(dateRange, '[1]', null)?.format('YYYY-MM-DD HH:mm:ss'),
      }

      onSubmit(values)
    },
    [onSubmit],
  )

  const handleChangedFields = useCallback(
    changedFields => {
      const changedEntityTypes = find(changedFields, ({ name }) =>
        includes(name, 'entityTypes'),
      )

      const changedSubtypes = find(changedFields, ({ name }) =>
        includes(name, 'subtypes'),
      )

      const { value: entityTypes } = changedEntityTypes || {
        value: selectedEntityTypes,
      }

      const { value: subtypes } = changedSubtypes || { value: selectedSubtypes }

      if (isNil(changedEntityTypes)) {
        setToDirty()

        if (!isNil(changedSubtypes)) {
          setSelectedSubtypes(subtypes)
        }

        return
      }

      let subtypesToSelect = []

      if (isEmpty(entityTypes) && !isEmpty(selectedEntityTypes)) {
        subtypesToSelect = []

        form.setFieldsValue({
          subtypes: subtypesToSelect,
        })
      }

      if (
        size(intersection(entityTypes, selectedEntityTypes)) !==
        size(selectedEntityTypes)
      ) {
        const newSubtypeOptions = map(
          uniqBy(
            flatten(
              map(
                filter(
                  keys(EVENT_SUBTYPES_GROUPED_BY_EVENT_METADATA_TYPE),
                  type => includes(entityTypes, type),
                ),
                type =>
                  get(EVENT_SUBTYPES_GROUPED_BY_EVENT_METADATA_TYPE, type),
              ),
            ),
            'value',
          ),
          'value',
        )

        const selectedSubtypesToRemove = difference(subtypes, newSubtypeOptions)

        if (!isEmpty(selectedSubtypesToRemove)) {
          subtypesToSelect = intersection(subtypes, newSubtypeOptions)

          form.setFieldsValue({
            subtypes: subtypesToSelect,
          })
        }
      }

      setSelectedSubtypes(subtypesToSelect)
      setSelectedEntityTypes(entityTypes)
      setToDirty()
    },
    [form, selectedEntityTypes, selectedSubtypes, setToDirty],
  )

  useEffect(() => {
    if (fetchUsersError) {
      showErrorNotification({
        title: 'Reports error',
        description: fetchUsersError,
      })
    }
  }, [fetchUsersError])

  if (isFetchUsersLoading || isGeneratingReport || isEmpty(userOptions)) {
    return <Spin />
  }

  return (
    <Form
      form={form}
      onFinish={handleFilter}
      onFieldsChange={handleChangedFields}
    >
      <Space>
        <FormSelect
          name="entityTypes"
          showSearch
          allowClear
          labelAbove
          label="Entity Types"
          filterOption={filterSelectOptions}
          options={entityTypeOptions}
          maxTagCount={2}
          mode="multiple"
          placeholder="Select entity types..."
          dropdownMatchSelectWidth={DROPDOWN_MIN_WIDTH}
          style={{ minWidth: 200 }}
          rules={[
            {
              required: true,
              min: 1,
              type: 'array',
            },
          ]}
        />
        <FormSelect
          name="types"
          showSearch
          allowClear
          labelAbove
          label="Event Types"
          filterOption={filterSelectOptions}
          options={typeOptions}
          maxTagCount={2}
          mode="multiple"
          placeholder="Select by event types..."
          dropdownMatchSelectWidth={DROPDOWN_MIN_WIDTH}
          style={{ minWidth: 200 }}
        />
        <FormSelect
          name="subtypes"
          showSearch
          allowClear
          labelAbove
          label="Event Subtypes"
          filterOption={filterSelectOptions}
          options={subtypeOptions}
          maxTagCount={2}
          mode="multiple"
          placeholder="Select by event subtypes..."
          dropdownMatchSelectWidth={DROPDOWN_MIN_WIDTH}
          style={{ minWidth: 200 }}
        />
      </Space>

      <Space>
        <FormSelect
          name="createdByIds"
          showSearch
          allowClear
          labelAbove
          label="Users"
          filterOption={filterSelectOptions}
          options={userOptions}
          maxTagCount={2}
          mode="multiple"
          placeholder="Select by users..."
          dropdownMatchSelectWidth={DROPDOWN_MIN_WIDTH}
          style={{ minWidth: 200 }}
          rules={[
            {
              required: true,
              min: 1,
              type: 'array',
            },
          ]}
        />
        <Form.Item
          label="Date Range"
          labelCol={getFormItemLabelAboveConfiguration(true)}
          name="dateRange"
        >
          <DatePicker.RangePicker
            label="Select Time Range"
            ranges={datePickerRangeOptions}
            format="YYYY/MM/DD HH:mm"
            showTime
          />
        </Form.Item>
      </Space>

      <FormButtons
        permissions={[PERMISSIONS.REPORT.GET_FOR_USERS]}
        isDisabled={!isDirty}
        onReset={resetForm}
        saveText="Search"
      />
    </Form>
  )
}

UsersReportForm.propTypes = {
  isGeneratingReport: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
}

UsersReportForm.defaultProps = {
  isGeneratingReport: false,
}

export default UsersReportForm
